/* eslint-disable react/no-unescaped-entities */
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Instagram, Globe, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/lib/divine";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/booking", label: "Booking" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      {/* Header */}
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-white/90 backdrop-blur-xl border-b border-black/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={BRAND.logo} alt="Divine Interiors" className="h-12 md:h-14 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wider uppercase transition-colors ${
                    isActive ? "text-[#D4A437]" : "text-[#0A0A0A]/75 hover:text-[#D4A437]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/booking"
              data-testid="header-book-btn"
              className="hidden md:inline-flex btn-gold text-xs"
            >
              Book Consultation <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <button
              className="md:hidden text-[#0A0A0A] p-2"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden bg-white border-t border-black/10">
            <div className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={`mnav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-widest uppercase ${
                      isActive ? "text-[#D4A437]" : "text-[#0A0A0A]"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              ))}
              <Link to="/booking" data-testid="mobile-book-btn" className="btn-gold w-fit">
                Book Consultation
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#FAF7F0] border-t border-black/5 mandala-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="overline mb-6">Let's Begin</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Let's build your
                <br />
                <span className="text-[#D4A437] italic">dream space.</span>
              </h2>
              <p className="text-[#6B6B6B] mt-6 max-w-md text-base leading-relaxed">
                Whether it's a soulful home, a commercial sanctuary, or a Vastu-aligned new beginning — we'd love to hear about it.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/booking" data-testid="footer-book-btn" className="btn-gold">
                  Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <a href={`tel:${BRAND.phoneRaw}`} data-testid="footer-call-btn" className="btn-ghost">
                  <Phone className="w-4 h-4" strokeWidth={1.5} /> Call
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 grid sm:grid-cols-2 gap-10">
              <div>
                <div className="overline mb-4">Contact</div>
                <div className="font-serif text-xl text-[#0A0A0A] mb-1">{BRAND.owner}</div>
                <div className="text-xs text-[#6B6B6B] mb-3">{BRAND.ownerTitle}</div>
                <a href={`tel:${BRAND.phoneRaw}`} className="block text-[#0A0A0A] hover:text-[#D4A437] transition-colors mb-2">
                  {BRAND.phone}
                </a>
                <a href={`https://${BRAND.website}`} className="block text-[#6B6B6B] hover:text-[#D4A437] transition-colors mb-2">
                  {BRAND.website}
                </a>
                <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="block text-[#6B6B6B] hover:text-[#D4A437] transition-colors">
                  @{BRAND.instagram}
                </a>
              </div>
              <div>
                <div className="overline mb-4">Studio</div>
                <p className="text-[#6B6B6B] leading-relaxed text-sm">
                  By appointment only.<br />
                  Consultations available across India.
                </p>
                <div className="flex items-center gap-4 mt-6">
                  <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-[#D4A437] hover:text-[#E5B547]">
                    <Instagram className="w-5 h-5" strokeWidth={1.5} />
                  </a>
                  <a href={`https://${BRAND.website}`} aria-label="Website" className="text-[#D4A437] hover:text-[#E5B547]">
                    <Globe className="w-5 h-5" strokeWidth={1.5} />
                  </a>
                  <a href={`tel:${BRAND.phoneRaw}`} aria-label="Phone" className="text-[#D4A437] hover:text-[#E5B547]">
                    <Phone className="w-5 h-5" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 pt-8 border-t border-black/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-[#6B6B6B]">
            <div>© {new Date().getFullYear()} Divine Interiors. Crafted with intention.</div>
            <Link to="/admin/login" data-testid="admin-link" className="hover:text-[#D4A437] tracking-widest uppercase">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
